import {
  getCurrentOrganizationContract,
  listTeamMembersContract,
  listMyOrganizationsContract,
  listMyInvitationsContract,
  acceptInvitationContract,
  declineInvitationContract,
  createOrganizationContract,
  setOrganizationNameContract,
  switchOrganizationContract,
  setMemberRoleContract,
  removeMemberContract,
  inviteTeamMemberContract,
  completeOnboardingContract,
  getIpAllowlistContract,
  getIpAccessStatusContract,
  setIpAllowlistContract,
} from '@repo/shared/common';
import { contractQueryOptions, createMutationHook } from '../query';

/** Hierarchical query key for the caller's current organization + role. */
export const currentOrganizationQueryKey = ['organizations', 'current'] as const;
/** Query key for the Settings → Team list (members + pending invites). */
export const teamQueryKey = ['organizations', 'team'] as const;
/** Query key for the org switcher's "organizations I belong to" list. */
export const myOrganizationsQueryKey = ['organizations', 'mine'] as const;
/** Query key for the caller's pending invitations (matched by verified email). */
export const myInvitationsQueryKey = ['organizations', 'invitations'] as const;
/** Query key for the Security → IP allowlist rules (admin view). */
export const ipAllowlistQueryKey = ['organizations', 'ip-allowlist'] as const;
/** Query key for whether THIS client's IP passes the active org's allowlist. */
export const ipAccessStatusQueryKey = ['organizations', 'ip-access-status'] as const;

/**
 * Query options for the caller's current organization + membership role.
 * Shared by the `_protected` loader (prefetch) and any component reading org/role.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `queryOptions` brand (see contractQueryOptions); the contract keeps it fully typed.
export function currentOrganizationQueryOptions() {
  return contractQueryOptions(getCurrentOrganizationContract, currentOrganizationQueryKey);
}

/**
 * Query options for the Settings → Team read (active members + pending invites).
 * Shared by the Team loader (prefetch) and the Team panel.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `queryOptions` brand (see contractQueryOptions); the contract keeps it fully typed.
export function teamQueryOptions() {
  return contractQueryOptions(listTeamMembersContract, teamQueryKey);
}

/**
 * Query options for the org switcher's "organizations I belong to" list (with the
 * active org id). Shared by the switcher loader (prefetch) and the switcher menu.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `queryOptions` brand (see contractQueryOptions); the contract keeps it fully typed.
export function myOrganizationsQueryOptions() {
  return contractQueryOptions(listMyOrganizationsContract, myOrganizationsQueryKey);
}

/**
 * Query options for the caller's pending invitations (invites addressed to
 * their verified email, excluding orgs they already belong to). Shared by the
 * onboarding invitations step (beforeLoad + component) and the signed-in
 * pending-invitations prompt.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `queryOptions` brand (see contractQueryOptions); the contract keeps it fully typed.
export function myInvitationsQueryOptions() {
  return contractQueryOptions(listMyInvitationsContract, myInvitationsQueryKey);
}

/**
 * Accepts one pending invitation (existing accounts). On success the caller has
 * JOINED AND SWITCHED INTO the accepting org — callers must treat it like an
 * org switch and `queryClient.clear()` (see the org-switch handling in
 * OrgCardMenu) so no other tenant's cached data survives; the hook itself only
 * invalidates the invitation list.
 */
export const useAcceptInvitation = createMutationHook(acceptInvitationContract, [
  myInvitationsQueryKey,
]);

/**
 * Declines one pending invitation. Returns the remaining invitations — callers
 * can `setQueryData` the response for an instant list update; the invalidation
 * here re-confirms in the background.
 */
export const useDeclineInvitation = createMutationHook(declineInvitationContract, [
  myInvitationsQueryKey,
]);

/**
 * Creates an organization (onboarding + sidebar). On success the caller is the
 * org's first admin and their active org has switched to it — invalidate the
 * current-org + my-orgs reads so the new org/role surfaces everywhere.
 */
export const useCreateOrganization = createMutationHook(createOrganizationContract, [
  currentOrganizationQueryKey,
  myOrganizationsQueryKey,
]);

/** Renames the current organization (admin). Invalidates the current-org read. */
export const useSetOrganizationName = createMutationHook(setOrganizationNameContract, [
  currentOrganizationQueryKey,
]);

/**
 * Switches the caller's active organization. Every org-scoped read must refetch
 * for the new tenant — invalidate the org reads PLUS the broad org-scoped data
 * keys (portfolio, uploads) so the whole app re-resolves against the new org.
 */
export const useSwitchOrganization = createMutationHook(switchOrganizationContract, [
  currentOrganizationQueryKey,
  myOrganizationsQueryKey,
  ['organizations'],
  ['portfolio'],
  ['uploads'],
]);

/** Sets a member's role (admin). Invalidates the Team list. */
export const useSetMemberRole = createMutationHook(setMemberRoleContract, [teamQueryKey]);

/** Removes a member (admin). Invalidates the Team list. */
export const useRemoveMember = createMutationHook(removeMemberContract, [teamQueryKey]);

/** Invites a teammate (admin). Invalidates the Team list. */
export const useInviteTeamMember = createMutationHook(inviteTeamMemberContract, [teamQueryKey]);

/**
 * Query options for the org's IP allowlist rules (Security settings, admin-only).
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `queryOptions` brand (see contractQueryOptions); the contract keeps it fully typed.
export function ipAllowlistQueryOptions() {
  return contractQueryOptions(getIpAllowlistContract, ipAllowlistQueryKey);
}

/**
 * Query options for "does THIS client's IP pass the active org's allowlist" — the
 * read the `_protected` gate and the blocked page consume. A short staleTime so a
 * client that moves networks (VPN on/off) re-checks promptly rather than riding a
 * stale allow/deny for the app's default 5-minute window.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `queryOptions` brand (see contractQueryOptions); the contract keeps it fully typed.
export function ipAccessStatusQueryOptions() {
  return {
    ...contractQueryOptions(getIpAccessStatusContract, ipAccessStatusQueryKey),
    staleTime: 30_000,
  };
}

/**
 * Replaces the org's IP allowlist (admin). Invalidates the allowlist read AND the
 * IP-access-status (the caller's own access may change the instant rules change).
 */
export const useSetIpAllowlist = createMutationHook(setIpAllowlistContract, [
  ipAllowlistQueryKey,
  ipAccessStatusQueryKey,
]);

/**
 * Resends a pending invite (admin). Re-posts the invite (idempotent on
 * org+email), which re-sends the invite email. A SEPARATE hook instance from
 * `useInviteTeamMember` so the per-row "Resend" busy state never bleeds into the
 * invite form's submit button.
 */
export const useResendInvite = createMutationHook(inviteTeamMemberContract, [teamQueryKey]);

/**
 * Finalizes onboarding (accept pending invites). On success the user may now have
 * an org + active-org pointer — invalidate the current-org + my-orgs reads.
 */
export const useCompleteOnboarding = createMutationHook(completeOnboardingContract, [
  currentOrganizationQueryKey,
  myOrganizationsQueryKey,
]);
